import type { Dispatch, FunctionComponent, SetStateAction } from "react";
import { useEffect, useState } from "react";
import style from "./Header.module.css";
import LogoNuevoPJ from "../../../assets/img/Logo_Nuevo_PJ.png";
import juriteca from "../../../assets/img/Header/Juriteca.svg";
import imageUserDefault from "../../../assets/icon/icon-user.svg";
import { useUserData } from "../../hooks/useUserData.hook";
import { storage } from "../../utils/Const.utils";
import { StorageKeys } from "../../types/enums/Storage.enum";
import type { PerfilEntidad } from "../../types/interfaces/user.interface";
import { RolEnum } from "../../types/enums/TipoRoles.enum";
import { Tooltip } from "@mui/material";
import { useVerificarExistenAlertas } from "@/shared/hooks/useNotificaciones.hook";
import { Link } from "react-router";
import { Routes } from "@/shared/types/enums/Routes.enum";
import { useTranslation } from "react-i18next";
import { NotificationBell } from "../NotificationBell";
import { RiMenuUnfold3Line } from "@remixicon/react";

interface HeaderProps {
    onToggleMenu: () => void;
    onToggleConfigMenu: () => void;
    isConfigMenuOpen?: boolean;
    openNotificaciones: () => void;
    setExistenAlertas: Dispatch<SetStateAction<boolean>>;
}

const Header: FunctionComponent<HeaderProps> = ({
    onToggleMenu,
    onToggleConfigMenu,
    isConfigMenuOpen = false,
    openNotificaciones,
    setExistenAlertas
}) => {
    const { t } = useTranslation();
    const {image = imageUserDefault} = useUserData();
    const [entityInfo, setEntityInfo] = useState<{ name: string; logo: string; idRol?: RolEnum } | null>(null);
    const perfil : PerfilEntidad = storage.get(StorageKeys.PERFIL);
    const { userData } = useUserData();

    useEffect(() => {
        const perfil: PerfilEntidad | null = storage.get(StorageKeys.PERFIL);
        
        // Verificar si el usuario es miembro de una entidad con roles específicos (abogado, paralegal, administrador)
        if (perfil?.infoEntidad) {
            const idRol = perfil.infoEntidad.relacion?.idRol;
            // Roles: Administrador = 1, Abogado = 2, Paralegal = 3
            const allowedRoles = [RolEnum.Administrador, RolEnum.Abogado, RolEnum.Paralegal];
            
            if (idRol && allowedRoles.includes(idRol)) {
                setEntityInfo({
                    name: perfil.infoEntidad.nombre,
                    logo: perfil.infoEntidad.image,
                    idRol: idRol
                });
            }
        }
    }, []);
    
    const {
        data: dataExistenAlertas,
        isLoading: isLoadingDataExistenAlertas
    } = useVerificarExistenAlertas({
        idUsuarioLogeado: userData?.id!,
        top: 10,
        visto: 0,
        idRelUsuarioEntidades: perfil?.infoEntidad?.relacion.idRelUsuarioEntidades || undefined,
        idRol: entityInfo?.idRol || undefined
    });

    useEffect(() => {
        if (dataExistenAlertas) {
            setExistenAlertas(dataExistenAlertas.data ? true : false);
        }
    }, [dataExistenAlertas]);

    return (
        <header className={`${style.ContentHeader} w-full h-[60px] bg-[#FFFFFF] border-[#f4f4f4] border flex justify-between py-1 px-10 max-[600px]:items-center max-[600px]:px-2`}>
            {/* <header className={`${style.ContentHeader} w-full h-[87px] bg-[#FFFFFF] border-[#f4f4f4] border flex justify-between py-2 px-10 max-[600px]:items-center max-[600px]:px-2`}> */}
            <div onClick={onToggleMenu} className={`${style.ButtonMenu} flex justify-center items-center gap-3 py-3 hover:bg-gray-200 cursor-pointer rounded-lg`}>
                <span className="flex justify-center items-center w-8 h-8 ">
                    <RiMenuUnfold3Line size={24} color={"#5B5B5B"} />
                </span>
            </div>
            <img className="w-[10rem] max-[600px]:w-auto max-[600px]:h-10 h-[3rem]" src={LogoNuevoPJ} alt="" />
            {/* <img className="w-[15rem] max-[600px]:w-auto max-[600px]:h-10 h-auto" src={LogoNuevoPJ} alt="" /> */}
            <div className="flex gap-4 items-center text-[1.3rem] hover:cursor-pointer">
                {entityInfo && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                        <img 
                            className="w-8 h-8 rounded-full border border-[#0050DA]" 
                            src={entityInfo.logo} 
                            alt={entityInfo.name}
                            onError={(e) => {
                                (e.currentTarget as HTMLImageElement).src = juriteca;
                            }}
                        />
                        <Tooltip arrow title={entityInfo.name}>
                            <span className="text-sm font-medium text-[#5E5D62] max-w-[150px] truncate max-[600px]:hidden">
                                {entityInfo.name}
                            </span>
                        </Tooltip>
                    </div>
                )}
                <Tooltip title={t('header.goToHome')} arrow placement="bottom-end">
                    <Link to={Routes.DEFAULT}>
                        <i className={`${style.IconMobile} flex d-icon d-user-in-house text-[#828282] hover:text-[#0050DA] hover:cursor-pointer`}></i>
                    </Link>
                </Tooltip>
                {!isLoadingDataExistenAlertas && dataExistenAlertas?.data ? (
                    <span className="relative inline-flex">
                        {/* animación "ping" al círculo rojo */}
                        <span className="flex absolute h-3 w-3 top-0 right-0 -mt-1 -mr-1">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#EC3340] opacity-75"></span>
                        {/* Círculo rojo estático que se mantiene visible */}
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-[#EC3340] border-1 border-white"></span>
                        </span>
                        {/* El ícono de notificación */}
                        <Tooltip title={t('header.Notifications')} arrow placement="bottom-end">
                            <i className={`flex d-icon d-notification text-[#828282] hover:text-[#0050DA] hover:cursor-pointer p-1`} onClick={openNotificaciones}></i>
                        </Tooltip>
                    </span>
                    ) : (
                        <Tooltip title={t('header.noPendingNotifications')} arrow placement="bottom-end">
                            <i className={`flex d-icon d-notification text-[#828282] hover:text-[#0050DA] hover:cursor-pointer p-1`} onClick={openNotificaciones}></i>
                        </Tooltip>
                    )
                }
                {/* Sistema de notificaciones SignalR + Push */}
                {/* <NotificationBell /> */}
                <Tooltip title={t('header.goToRole')} arrow placement="bottom-end">
                    <a 
                        href={'https://rolnacionalaudiencias.poderjudicial.gob.do/'}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none' }}
                    >
                        <i className={`${style.IconMobile} flex d-icon d-calendar text-[#828282] hover:text-[#0050DA] hover:cursor-pointer`}></i>
                    </a>
                </Tooltip>
                <Tooltip title={t('header.goToJuriteca')} arrow placement="bottom-end">
                    <a 
                        href={'https://juriteca.edu.do/'}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none' }}
                    >
                        <img className={`${style.IconMobile} w-8 h-8 rounded-full border-1 border-[#0050DA] hover:cursor-pointer`} src={juriteca} alt="Juriteca" />
                    </a>
                </Tooltip>
                <span onClick={onToggleConfigMenu} data-menu-trigger className={"flex flex-row bg-[#D9D9D9] w-[5rem] justify-between items-center rounded-full"}>
                    <img className={"w-10 h-10 rounded-full border-1 border-[#f4f4f4] hover:cursor-pointer"} src={image} alt="Imagen usuario logueado" />
                    <i className={`flex transition-transform d-icon d-arrowhead-down text-[#5E5D62] mr-2 ${isConfigMenuOpen ? 'rotate-90' : ''}`}></i>
                </span>
            </div>
        </header>
    );
}

export default Header;